import MerchantLogo from '@/components/cashflow/MerchantLogo';
import { Button } from '@/components/common/Button';
import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { Plus } from '@/components/common/icons';
import { useToasts } from '@/components/common/Toasts';
import {
    Paragraph,
    Subheader,
    Text,
    Title,
} from '@/components/common/Typography';
import { useModal } from '@/components/ModalManager';
import { toDollars } from '@/util/currency';
import { logError } from '@/util/logger';
import { Box, ModalCloseButton } from '@chakra-ui/react';
import { isApolloErrorWithCustomCode } from '@common/apollo';
import {
    CashflowTransactionEntry,
    useSplitFormProps,
} from '@common/react/cashflow';
import { TOASTS } from '@common/toasts';
import { toDollarsWithCents } from '@common/util/currency';
import { dt } from '@common/util/dateTime';
import { useRef } from 'react';
import SplitBlock from './SplitBlock';

interface SplitTransactionEntryProps {
    transactionEntry: CashflowTransactionEntry;
    onClickBack: VoidFunction;
}

const SplitTransactionEntry = ({
    transactionEntry,
    onClickBack,
}: SplitTransactionEntryProps) => {
    const { showSuccessToast, showErrorToast } = useToasts();
    const modal = useModal();
    const lastSplitRef = useRef<HTMLDivElement>(null);
    const {
        splits,
        handleAddSplit,
        handleDeleteSplit,
        handleSplitUpdate,
        disabled,
        handleSplitTransaction,
        isSubmitting,
        leftToSplit,
    } = useSplitFormProps({
        transactionEntry,
        onSuccess: () => {
            showSuccessToast(TOASTS.splitTransactionSuccess);
            modal.remove();
        },
        onError: (e) => {
            logError(e);

            if (isApolloErrorWithCustomCode(e, 'INVALID_SPLIT_AMOUNTS')) {
                showErrorToast(TOASTS.splitTransactionAmountError);
            } else {
                showErrorToast(TOASTS.splitTransactionGenericError);
            }
        },
        onAddSplit: () => {
            lastSplitRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        },
    });

    return (
        <Box overflowY="scroll" borderRadius={6}>
            <ModalCloseButton size="lg" color="textMuted" zIndex="modal" />
            <Box
                top={0}
                pt={8}
                px={8}
                pb={4}
                bg="white"
                zIndex="sticky"
                borderBottom="1px solid"
                borderColor="border"
                position="sticky"
                borderTopRightRadius={6}
                borderTopLeftRadius={6}
            >
                <Subheader>Split Transaction</Subheader>
                <Paragraph>
                    Amount {leftToSplit >= 0 ? 'remaining' : 'over'}:{' '}
                    {toDollarsWithCents(Math.abs(leftToSplit))}
                </Paragraph>
            </Box>
            <Box px={8} pt={2} bg="gray.100">
                <FlexRow gap={2} align="center" mt={1}>
                    {transactionEntry.merchant ? (
                        <FlexRow align="center" gap={2}>
                            <MerchantLogo
                                merchant={transactionEntry.merchant}
                            />
                            <Text>{transactionEntry.merchant.name}</Text>
                        </FlexRow>
                    ) : (
                        transactionEntry.name
                    )}
                    <Text color="gray.500" ml={2} mb="1px">
                        {dt(transactionEntry.date).format('MMMM D, YYYY')}
                    </Text>
                </FlexRow>
                <Title mt={2}>
                    {toDollars(transactionEntry.amount, {
                        withCents: true,
                        alwaysSigned: true,
                    })}
                </Title>
                <FlexCol gap={4} mt={4}>
                    {splits.map((split, index) => (
                        <SplitBlock
                            key={index}
                            split={split}
                            index={index}
                            onUpdate={(updates) =>
                                handleSplitUpdate(updates, index)
                            }
                            leftToSplit={leftToSplit}
                            onDelete={handleDeleteSplit}
                            canDelete={splits.length > 2}
                            isPositiveTransaction={transactionEntry.amount > 0}
                            ref={
                                index === splits.length - 1
                                    ? lastSplitRef
                                    : undefined
                            }
                        />
                    ))}
                </FlexCol>
                <ButtonWithIcon
                    onClickEvent="Add Split Button Clicked"
                    my={4}
                    variant="outline"
                    colorScheme="gray"
                    h={10}
                    px={5}
                    iconLeft={<Plus />}
                    onClick={() => handleAddSplit({ isExpanded: false })}
                >
                    Add split
                </ButtonWithIcon>
            </Box>
            <FlexRow
                bottom={0}
                py={4}
                bg="white"
                zIndex="sticky"
                position="sticky"
                borderTopWidth={1}
                align="center"
                px={8}
                justify="space-between"
            >
                <Button
                    onClick={onClickBack}
                    onClickEvent="Cancel Transaction Split Button Clicked"
                    variant="outline"
                    h={12}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSplitTransaction}
                    onClickEvent="Split Transaction Submit Button Clicked"
                    isDisabled={disabled}
                    isLoading={isSubmitting}
                    h={12}
                >
                    Split transaction
                </Button>
            </FlexRow>
        </Box>
    );
};

export default SplitTransactionEntry;
